import clsx from "clsx";
import get from "lodash/get";
import pluralEN from "plural";
import pluralRU from "plural-ru";
import React, { FC, useEffect, useRef, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal } from '@pd/pd-ui-kit';
import { debounce } from "lodash";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";

import ProofDocumentControl from "~/components/Forms/ViolationRecordingForm/ProofDocumentControl";
import IconMinorAlert from "~/components/Icons/alert-circle";
import IconYellowAlert from "~/components/Icons/alert-square";
import IconRedAlert from "~/components/Icons/alert-triangle";
import Enter from "~/components/Icons/arrow-enter";
import DocIcon from "~/components/Icons/icon-doc";
import Preloader from "~/components/Preloader";
import Controls from "~/pages/Violations/ViolationDescription/Controls";
import Button from "~/shared/ui/Fields/Button";
import Input from "~/shared/ui/Fields/Input";
import Wrapper from "~/shared/ui/Wrapper";
import { ROLE_TYPES } from "~/constans/roles";
import { DealerInfoPanel } from "~/entity/Dealer/ui/DealerInfoPanel";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { dateDifIgnoreWeekends } from "~/libs/dateDifIgnoreWeekends";
import { getShortUserName, language } from "~/libs/getData";
import { getNumberDayMonthYear } from "~/libs/getDateOptions";
import { getFormatDate } from "~/libs/getFormatDate";
import { uploadFile } from "~/libs/uploadFile";
import {
  useAddCommentMutation,
  useGetProfileQuery,
  useGetViolationByIdQuery,
  useGetViolationCommentsQuery,
  ViolationStatus,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";

import SanctionsModal from "./SanctionsModal/index";
import { VIOLATION_OPTION, VIOLATION_TYPE_OPTION } from "./options";

import s from "./violationDescription.less";

interface IProps {}

const IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/pjpeg"];
const isImageFile = (file) =>
  file && file.mimeType && IMAGE_MIME_TYPES.includes(file.mimeType);
const isOtherFile = (file) =>
  file && file.mimeType && !IMAGE_MIME_TYPES.includes(file.mimeType);

const ViolationDescription: FC<IProps> = () => {
  const isEn = language === "en";
  const { t } = useTranslation();
  const { params } = useRouteMatch();
  const personalId = params["id"];
  const { openModal, page } = useGetLocationData();
  const history = useHistory();
  const goBack = () => {
    history.push("/violations/");
    page && setData("page", page);
  };
  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);
  const [fileId, setFileId] = useState([]);
  const [comments, setComments] = useState([]);
  const listRef = useRef<HTMLUListElement | null>(null);
  const profile = useGetProfileQuery({});
  const { hasPermission, loading: loadingPermissions } = usePermissions();

  const profileLoading = profile.loading;
  const profileId = get(profile.data, "UserGetter.getProfile.id");

  const [uploadFileLoader, setUploadFileLoader] = useState(false);
  const { notifyError, notifySuccess } = useNotify();
  const schema = yup.object().shape({
    comment: yup
      .string()
      .test(
        "comment",
        t("validation.required"),
        () => !watch("comment") || !!watch("comment").trim()
      )
      .required(t("validation.required")),
  });

  const { handleSubmit, control, errors, reset, watch } = useForm({
    defaultValues: {
      comment: "",
    },
    resolver: yupResolver(schema),
  });

  const { data, loading, refetch } = useGetViolationByIdQuery({
    variables: {
      id: personalId,
    },
    fetchPolicy: "no-cache",
  });

  // @ts-ignore
  const {
    Dealer,
    ViolationType,
    fixDeadline,
    reportedByUser,
    category,
    punishment,
    createdAt,
    description,
    File,
    DocDate,
    DocNum,
    status,
  } = get(data, "ViolationGetter.getById", {}) || {};

  const violationData = {
    description,
  };

  const deadlineCounter = Math.abs(
    dateDifIgnoreWeekends(new Date(), fixDeadline),
  );

  const statusFixed = status === ViolationStatus.Fixed;
  const statusDeleted = status === ViolationStatus.Deleted;
  const statusRejected = status === ViolationStatus.Reject;

  const pluralDays = isEn
    ? pluralEN("day", "days", deadlineCounter)
    : pluralRU(deadlineCounter, "день", "дня", "дней");

  const violationComments = useGetViolationCommentsQuery({
    variables: {
      count: 25,
      id: personalId,
    },
    fetchPolicy: "no-cache",
  });

  const Comments = get(
    violationComments,
    "data.ViolationGetter.getComments.items",
    []
  );

  useEffect(() => {
    if (Comments.length && !violationComments.loading) {
      setComments([...Comments]);
    }
  }, [Comments]);

  const [addCommentMutation] = useAddCommentMutation({
    onCompleted: () => {
      if (listRef.current) {
        listRef.current.scrollTop = 0;
      }
      reset();
      setFileId([]);
      violationComments.refetch();
    },
  });
  const isFiles = fileId.length > 0;

  const onSubmit = ({ comment }) => {
    if (comment && comment.trim().length > 0) {
      addCommentMutation({
        variables: {
          id: personalId,
          comment,
          files: isFiles ? fileId : [],
        },
      });
    }
  };

  const getCommentsByScroll = debounce((e) => {
    if (e.target.scrollTop > e.target.scrollHeight / 2) {
      violationComments.fetchMore({
        variables: {
          page: Math.ceil(Comments.length / 25),
        },
        updateQuery: ({ ViolationGetter }, { fetchMoreResult }) => {
          if (!fetchMoreResult) return { ViolationGetter };
          setComments([
            ...ViolationGetter.getComments.items,
            ...get(fetchMoreResult, "ViolationGetter.getComments.items"),
          ]);
        },
      });
    }
  }, 300);

  const categoryIco = () => {
    switch (ViolationType.category) {
      case "critical":
        return <IconRedAlert />;
      case "operational":
        return <IconYellowAlert />;
      case "minor":
        return <IconMinorAlert />;
    }
  };

  const closeModal = () => setDataReplace("openModal", "");

  const addViolationOption = () => {
    return punishment ? ["punishment"] : [];
  };

  const dealerCanResponseCheck = (): boolean => {
    if (comments.length > 0) {
      const lastCommentUser = `${comments[0]?.User?.roleEn[0]?.toLowerCase()}${comments[0].User.roleEn.substr(
        1
      )}`;
      const isDealer = ROLE_TYPES.DEALER.name;
      const isHolding = ROLE_TYPES.HOLDING.name;

      return lastCommentUser === isHolding || lastCommentUser === isDealer;
    }
  };
  const [dealerWroteLastComment, setDealerWroteLastComment] = useState(false);

  useEffect(() => {
    setDealerWroteLastComment(dealerCanResponseCheck());
  }, [comments]);
  if (loading || profileLoading || loadingPermissions) {
    return <Preloader />;
  }

  return (
    <Wrapper.Content
      title={t("violation.title")}
      txtBtn={t("ui.back")}
      goBackHandler={goBack}
    >
      <div className={s.wrapper}>
        <div className={s.wrapper_edition}>
        <div className={s.topSector}>
          {hasPermission("violations.getById") && (
            <DealerInfoPanel Dealer={Dealer} />
          )}
          <Controls
            {...{
              page,
              status,
              refetch,
              personalId,
              deadlineCounter,
              dealerWroteLastComment,
            }}
          />
        </div>
        <div className={s.wrapper_description}>
          <div className={s.wrapper_right}>
            {[...VIOLATION_TYPE_OPTION, ...addViolationOption()].map((item) => (
              <div key={item} className={s.item}>
                <div className={s.item_title}>{t(`violation.${item}Type`)}</div>
                {ViolationType && item === "Subdivision" ? (
                  <div className={s.item_description}>
                    {ViolationType[item]?.name}{" "}
                  </div>
                ) : reportedByUser && item === "reportedByUser" ? (
                  <div className={s.item_description}>
                    {reportedByUser?.fullname}{" "}
                  </div>
                ) : (
                  <div className={s.item_description}>
                    {item === "punishment" ? punishment : ViolationType[item]}
                  </div>
                )}
              </div>
            ))}
            {[...VIOLATION_OPTION].map((item) => (
              <div key={item} className={s.item}>
                <div className={s.item_title}>{t(`violation.${item}`)}</div>
                <div className={s.item_description}>{violationData[item]}</div>
              </div>
            ))}
          </div>

          <div className={s.wrapper_left}>
            <div className={s.info}>
              <div className={s.info__text}>
                <div className={s.fixDeadline}>
                  {t("violation.eliminateProblemBefore")}:
                  <span>{` ${getFormatDate(fixDeadline, true)}`}</span>
                </div>
                <div className={s.fixDeadline}>
                  {dateDifIgnoreWeekends(new Date(), fixDeadline) < 0
                    ? t("violation.timer_pros")
                    : t("violation.timer")}
                  : <span>{` ${deadlineCounter} ${pluralDays}`}</span>
                </div>
              </div>
              {categoryIco()}
            </div>
            <div className={s.info}>
              <div className={s.info__text}>
                {DocNum && (
                  <div className={s.fixDeadline}>
                    {t("violationsTable.DocNum")}
                    <span>{` ${DocNum} `}</span>
                  </div>
                )}
                {DocDate && (
                  <div className={s.fixDeadline}>
                    {t("violationsTable.DocDate")}
                    <span>{getNumberDayMonthYear({ date: DocDate })}</span>
                  </div>
                )}
                {createdAt && (
                  <div className={s.fixDeadline}>
                    {t("violationsTable.DocDatePublish")}
                    <span>{getNumberDayMonthYear({ date: createdAt })}</span>
                  </div>
                )}
              </div>
            </div>

            {File && Object.keys(File).length > 0 && (
              <div className={s.info}>
                <div className={s.fixDeadline}>
                  {t("violation.downloadFile")}
                </div>
                <a href={File?.link} download>
                  <DocIcon />
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={clsx(s.wrapper_edition, s.wrapper_edition__shadow)}>
        <div className={s.item_comment}>
          <div className={s.wrapper_comments}>
            <div className={s.item_comment_title}>
              {t("violation.commentsTitle")}
            </div>
            <ul
              ref={listRef}
              className={s.list}
              onScrollCapture={(e) => {
                e.persist();
                getCommentsByScroll(e);
              }}
            >
              {hasPermission("violations.getComments") &&
                comments &&
                comments.map(({ User, comment, createdAt, Files }, id) => (
                  <li key={id} className={s.list_item}>
                    <div className={s.name}>
                      {getShortUserName(isEn ? User.fullnameEn : User.fullname)}
                      <span>{getFormatDate(createdAt, true)}</span>
                    </div>
                    <div
                      className={clsx(s.commentSettings, {
                        [s.comment]: User.id === profileId,
                        [s.sideComment]: User.id !== profileId,
                      })}
                    >
                      {comment}
                    </div>
                    <div className={s.fileWrapper}>
                      <div className={s.imageFileWrapper}>
                        {Files.filter(isImageFile).map((file) => (
                          <div key={file.id} className={s.wrapper_img_file}>
                            <a href={file.link} download>
                              <img src={file.link} />
                            </a>
                          </div>
                        ))}
                      </div>
                      <div className={s.textFileWrapper}>
                        {Files.filter(isOtherFile).map((file) => (
                          <div key={file.id} className={s.wrapper_text_file}>
                            <a href={file.link} download>
                              <div className={s.text_file}>{file.name}</div>
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          </div>

          {!statusFixed &&
            !statusDeleted &&
            !statusRejected &&
            hasPermission("violations.addComment") && (
              <form
                className={s.form_comment}
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className={s.form_wrapper}>
                  <Input
                    name="comment"
                    placeholder={t("violation.commentPlaceholder")}
                    control={control}
                    customClassName={s.select_width}
                    error={errors.comment && errors.comment.message}
                  />

                  <Button
                    txtBtn=""
                    isForm
                    customClassName={s.btnIcon}
                    onlyIcon
                    // loading={grVersionMutation.loading}
                  >
                    <Enter />
                  </Button>
                </div>

                <Field
                  className={s.upload}
                  type="file"
                  name="file"
                  control={control}
                  loading={uploadFileLoader}
                  onUpload={async (file) => {
                    setUploadFileLoader(true);
                    try {
                      const uploaded = await uploadFile(file, () =>
                        notifyError(t("common.data_uploaded_error")),
                      );
                      if (uploaded) {
                        // Файлов может быть несколько: id уходит в общий список,
                        // а onUpload ничего не возвращает — значение поля остаётся
                        // null, и FileField снова показывает кнопку загрузки.
                        setFileId((old) => [...old, uploaded.id]);
                        notifySuccess(t("common.file_uploaded_successfully"));
                      }
                    } finally {
                      setUploadFileLoader(false);
                    }
                  }}
                />

                {isFiles && (
                  <div className={s.wrapper_files}>
                    <div className={s.title_files}>Прикрепленые файлы</div>
                    <div className={s.items_comments}>
                      {fileId.map((id) => (
                        <ProofDocumentControl
                          filesId={fileId}
                          setFilesId={setFileId}
                          id={id}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </form>
            )}
        </div>
      </div>
        <Modal
          open={openModal === "sanctions"}
          onCancel={closeModal}
          title={t("violation.sanctionsTitle")}
          closable
          centered
          destroyOnHidden
          className={s.modal}
        >
          <SanctionsModal
            refetch={refetch}
            closeModal={closeModal}
            id={personalId}
          />
        </Modal>
      </div>
    </Wrapper.Content>
  );
};

export default ViolationDescription;
